import {
  archesSchema,
  getArchesCoveredExactly,
  getArchFromTooth,
  getTeethFromArch,
  isArchScopedItem,
  ORDERED_ARCHES,
  type ItemType,
  type TeethArch,
} from "@jasper/client-models";
import { TeethSelect, validateSchema } from "@jasper/forms";
import { Stack, Text } from "@mantine/core";
import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { ConfigurableProduct, ProductStepProps } from "./types";
import { useRegisterStepSave } from "./useRegisterStepSave";
import { useTeethSelectionDescription } from "./TeethSteps/useTeethSelectionDescription";
import {
  useTeethSelectionSubmission,
  type FormatTeethSelection,
} from "./TeethSteps/useTeethSelectionSubmission";

/*
  Validated as an object rather than a bare array: `validateSchema` drops issues whose path
  is empty, so a root-level failure would never reach the form. Same shape as the teeth
  steps, which read their message under the `arches` key.
*/
const archesFormSchema = z.object({ arches: archesSchema });

export type ArchStepProps = ProductStepProps & {
  applyArchesSelection?: FormatTeethSelection<number>;
};

const defaultApplyArchesSelection = async ({
  teeth,
  product,
  itemType,
}: {
  teeth: number[];
  itemType: ItemType;
  product: ConfigurableProduct;
}) => {
  const arch = getArchFromTooth(teeth[0]);
  const itemsToSave = arch ? [{ type: itemType, arch }] : [];

  return {
    productId: product.id,
    teeth: teeth,
    items: itemsToSave,
  };
};

export const ArchStep = ({
  stepKey,
  products,
  itemType,
  orderConfiguration,
  applyArchesSelection = defaultApplyArchesSelection,
  registerStepSave,
}: ArchStepProps) => {
  const { t } = useTranslation("product-workflows");
  const describeTeethSelection = useTeethSelectionDescription();
  /*
    Every arch the products cover, and not just the first product's: a bimaxillary wax
    bite is two products, one per arch, and V1 saves it as one product holding two arch
    items. Submitting then splits them into one product per arch, as V2 saves them.
  */
  const initialArches = useMemo(() => {
    const coveredArches = products.flatMap(product =>
      product.items.filter(isArchScopedItem).map(item => item.arch)
    );
    return ORDERED_ARCHES.filter(arch => coveredArches.includes(arch));
  }, [products]);

  const submitTeethSelection = useTeethSelectionSubmission({
    products,
    itemType,
    orderConfiguration,
    formatTeethSelection: applyArchesSelection,
  });

  const defaultValues = useMemo(
    () => ({
      product: {
        arches: initialArches,
      },
    }),
    [initialArches]
  );

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: async ({ value }) => {
      const arches = ORDERED_ARCHES.filter(arch =>
        value.product.arches.includes(arch)
      );
      const validation = validateSchema(archesFormSchema, { arches });

      if (!validation.success) {
        return;
      }

      // One product per arch.
      await submitTeethSelection(
        arches.map(arch => [...getTeethFromArch(arch)])
      );
    },
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  const validateSelection = ({
    value,
  }: {
    value: { arches: TeethArch[] };
  }): Record<string, unknown> | undefined => {
    const arches = ORDERED_ARCHES.filter(arch => value.arches.includes(arch));
    const result = validateSchema(archesFormSchema, { arches });
    return result.success ? undefined : result.errors;
  };

  const toggleArch = (arch: TeethArch, currentArches: TeethArch[]) => {
    const newArches = currentArches.includes(arch)
      ? currentArches.filter(selected => selected !== arch)
      : [...currentArches, arch];
    return newArches;
  };

  return (
    <Stack h="100%" mih={0} p="md" gap="md">
      <Stack gap="md" flex={1} mih={0}>
        <Text c="dimmed" size="sm">
          {t("steps.arch.hint")}
        </Text>
        <form.Field
          name="product"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const errors = validateSelection({ value });

              if (!errors) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return errors;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            const errors =
              typeof formErrors?.arches === "string"
                ? t(formErrors?.arches, {
                    keyPrefix: "steps.arch.errors",
                    defaultValue: t("errorOccurred", { keyPrefix: "common" }),
                  })
                : undefined;

            const teethToShow =
              field.state.value.arches?.flatMap(getTeethFromArch) ?? [];

            return (
              <TeethSelect
                onBlur={() => field.handleBlur()}
                teeth={teethToShow}
                teethError={errors ? teethToShow : []}
                error={errors}
                onClick={toothId => {
                  const arch = getArchFromTooth(toothId);
                  if (arch) {
                    const newArches = toggleArch(
                      arch,
                      field.state.value.arches
                    );
                    field.setValue({
                      ...field.state.value,
                      arches: newArches,
                    });
                  }
                }}
                getTeethDescription={({ teeth }) =>
                  describeTeethSelection(teeth, [
                    {
                      type: itemType,
                      count: getArchesCoveredExactly(teeth).length,
                    },
                  ])
                }
              />
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
